import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { storage, auth } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { tcService } from '../services/tc.service';
import { formatUSPhoneInput, toE164US } from '../utils/phone';
import { getViewerTimezone } from '../utils/dateTime';
import './ProfilePage.css';
import stateCityData from '../data/state_city.json';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import NotificationSettingsPage from './NotificationSettingsPage';
import PaymentsPage from './PaymentsPage';
import ConnectedServicesPage from './ConnectedServicesPage';
import UserActivityPage from './UserActivityPage';
import SettingsPage from './SettingsPage';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import TermsOfServicePage from './TermsOfServicePage';

// Brokerage/Company "License #" field with an inline "Verify" pill button —
// mirrors mobile EditProfileScreen's renderLicenseWithVerify: idle shows the
// Verify button, validating shows a spinner, valid shows a green check,
// invalid re-shows Verify so the user can retry. Shared by the Agent/TC
// "Brokerage License #" row and the Broker "License Number" row.
const LicenseVerifyField = ({ label, name, value, onChange, verify, onVerify, isEditing }) => (
  <div className="info-field">
    <label className="info-label">{label}</label>
    {isEditing ? (
      <>
        <div className="license-verify-row">
          <input
            type="text"
            name={name}
            className="profile-input license-verify-input"
            value={value}
            onChange={onChange}
            placeholder="Enter license number"
          />
          {verify.status === 'validating' ? (
            <span className="license-verify-spinner" aria-label="Verifying" />
          ) : verify.status === 'valid' ? (
            <span className="license-verify-check" aria-label="Verified">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </span>
          ) : (
            <button type="button" className="license-verify-btn" onClick={onVerify}>Verify</button>
          )}
        </div>
        {!!verify.message && (
          <span className={`license-verify-msg license-verify-msg-${verify.status}`}>{verify.message}</span>
        )}
      </>
    ) : (
      <p className="info-value">{value || ''}</p>
    )}
  </div>
);

const ProfilePage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [userLicenseState, setUserLicenseState] = useState('');
  const [designatedBrokerData, setDesignatedBrokerData] = useState(null);
  // Raw brokerage firm profile (companyName, companyLicenseNumber, designatedBrokerEmail
  // on file, etc.) — kept alongside formData so the Resend-invite handler has access to
  // fields that aren't otherwise surfaced in the UI (mirrors mobile's `brokerageProfile`).
  const [brokerageProfileData, setBrokerageProfileData] = useState(null);
  // TC's own self-entered brokerage fields (from tc-profile) and the firm-side
  // joined record (from /tc/brokerage) — merged for display, canonical data wins.
  // Mirrors mobile EditProfileScreen's tcProfile/tcBrokerage split.
  const [tcProfileData, setTcProfileData] = useState(null);
  const [tcBrokerageData, setTcBrokerageData] = useState(null);
  const [isResendingDbInvite, setIsResendingDbInvite] = useState(false);
  // Brokerage License # "Verify" flow — shared between Agent and TC (same
  // brokerLicenseNumber field, mutually exclusive active roles) — and a
  // separate one for Broker's own Company "License Number" field. Mirrors
  // mobile EditProfileScreen's per-field verify status/message pair.
  const [brokerageLicenseVerify, setBrokerageLicenseVerify] = useState({ status: 'idle', message: '' });
  const [companyLicenseVerify, setCompanyLicenseVerify] = useState({ status: 'idle', message: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  // Left sidenav section for "My Profile" — everything the old profile-menu
  // dropdown used to list (Notification Settings, Payments, Connected
  // Services, Activity Log, Security & Settings, Privacy, Terms) now lives
  // here as sections instead of separate menu items (see DashboardHeader.js).
  const [activeProfileSection, setActiveProfileSection] = useState('info');
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    state: '',
    city: '',
    zipCode: '',
    personalState: '',
    personalCity: '',
    personalZipCode: '',
    role: '',
    profilePhotoUrl: '',
    // Agent-specific fields
    agentNumber: '',
    narId: '',
    licenseNumber: '',
    memberSince: '',
    // Brokerage fields (agent- and TC-specific — same field names shared by
    // both roles, since only one is ever the active role at a time)
    brokerageName: '',
    brokerOfRecordName: '',
    brokerLicenseNumber: '',
    brokerageLicenseType: '',
    brokerageAddress: '',
    brokeragePhone: '',
    brokerageNarId: '',
    // Company fields (broker-specific)
    companyName: '',
    companyLicenseNumber: '',
    companyLicenseType: '',
    companyLicenseState: '',
    companyAddress: '',
    companyPhone: '',
    companyNarId: '',
    // Firm fields (brokerage role)
    firmLegalName: '',
    firmLicenseNumber: '',
    firmLicenseState: '',
    firmDesignatedBrokerName: '',
    firmAddress: '',
    firmPhone: '',
    firmEmail: '',
    firmNarId: '',
    firmBrokerageNarId: '',
    // Designated Broker (read-only, brokerage role) — linked BROKER-role user
    designatedBrokerName: '',
    designatedBrokerEmail: '',
    designatedBrokerPhone: '',
    designatedBrokerStatus: ''
  });

  console.log("==userData==",JSON.stringify(userData));

  // Get user role from roles array or role field
  const getUserRole = (data) => {
    if (data?.roles && Array.isArray(data.roles) && data.roles.length > 0) {
      // Check for transaction coordinator first (distinct from agent)
      const hasTC = data.roles.some(role => role.toLowerCase() === 'transaction_coordinator');
      if (hasTC) return 'transaction_coordinator';

      // Check for agent role
      const hasAgent = data.roles.some(role =>
        role.toLowerCase() === 'agent' || role.toLowerCase() === 'role_agent'
      );
      if (hasAgent) return 'agent';

      // Check for broker role (broker / broker_admin / broker_manager)
      const hasBroker = data.roles.some(role => {
        const r = role.toLowerCase().replace(/^role_/, '');
        return r === 'broker' || r === 'broker_admin' || r === 'broker_manager';
      });
      if (hasBroker) return data.roles[0].replace(/^ROLE_/i, '').toLowerCase();

      // Return first role, removing 'ROLE_' prefix if present
      const firstRole = data.roles[0];
      return firstRole.replace(/^ROLE_/i, '').toLowerCase();
    }
    return data?.role?.toLowerCase() || '';
  };

  // Check if user has agent role in roles array (TC is NOT an agent)
  const isUserAgent = (userData) => {
    if (userData?.roles && Array.isArray(userData.roles)) {
      return userData.roles.some(role => {
        const r = role.toLowerCase().replace(/^role_/, '');
        return r === 'agent' || r === 'buyer_agent' || r === 'listing_agent';
      });
    }
    // Fallback to role field
    const r = userData?.role?.toLowerCase() || '';
    return r === 'agent' || r === 'buyer_agent' || r === 'listing_agent';
  };

  // Check if user has agent role (TC is NOT an agent)
  const agentRoles = ['agent', 'buyer_agent', 'listing_agent'];
  // Check if user has broker role (includes broker_admin, broker_manager)
  const brokerRoleSet = ['broker', 'broker_admin', 'broker_manager'];

  // Returns the correct broker profile endpoint for the given role
  const getBrokerProfileEndpoint = (userId, role) => {
    const r = (role || '').toLowerCase();
    if (r === 'broker_manager') return API_ENDPOINTS.brokerManagerProfile(userId);
    if (r === 'broker_admin') return API_ENDPOINTS.brokerAdminProfile(userId);
    return API_ENDPOINTS.brokerProfile(userId);
  };

  const isUserBroker = (userData) => {
    if (userData?.roles && Array.isArray(userData.roles)) {
      return userData.roles.some(role => {
        const r = role.toLowerCase().replace(/^role_/, '');
        return brokerRoleSet.includes(r);
      });
    }
    const r = userData?.role?.toLowerCase() || '';
    return brokerRoleSet.includes(r);
  };

  const isUserBrokerage = (userData) => {
    const activeRole = getActiveRoleFromStorage()?.toLowerCase();
    if (activeRole) return activeRole === 'brokerage';
    if (userData?.roles && Array.isArray(userData.roles)) {
      return userData.roles.some(r => r.toLowerCase().replace(/^role_/, '') === 'brokerage');
    }
    return (userData?.role || '').toLowerCase() === 'brokerage';
  };

  // Fetch brokerage firm profile
  const fetchBrokerageProfile = async (firebaseToken) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.brokerageProfile, {
        headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' }
      });
      const raw = response.data;
      return (raw?.data && typeof raw.data === 'object') ? raw.data : raw;
    } catch (error) {
      console.error('Error fetching brokerage profile:', error.response?.status, error.message);
      return null;
    }
  };

  // Fetch the brokerage's linked brokers and resolve the Designated Broker
  // (matches mobile: GET /api/v1/brokerage/brokers, matched against
  // brokerageProfile.designatedBrokerUid, falling back to the first linked broker)
  const fetchDesignatedBroker = async (firebaseToken, brokerageData) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.brokerageLinkedBrokers, {
        headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' }
      });
      const raw = response.data;
      const list = raw?.content || raw?.data || raw;
      const linked = Array.isArray(list) ? list : [];
      if (linked.length === 0) return null;
      const designatedUid = brokerageData?.designatedBrokerUid;
      return linked.find(b => b.userId === designatedUid) || linked[0];
    } catch (error) {
      console.error('Error fetching linked brokers:', error.response?.status, error.message);
      return null;
    }
  };

  // Function to populate form data from API response
  const populateFormData = (data, agentData = null, brokerData = null, brokerageData = null, designatedBroker = null, tcData = null) => {
    // Use stateCode or state for location; backend returns 'state' for address state
    const stateValue = data.stateCode || data.state || '';
    // Backend returns postalCode (mobile field name), fallback to zipCode
    const zipValue = data.postalCode || data.zipCode || '';

    // Get role: active role from localStorage takes priority over roles array
    const activeStoredRole = getActiveRoleFromStorage()?.toLowerCase();
    const userRole = activeStoredRole || getUserRole(data);

    console.log('Profile - populateFormData - stateValue:', stateValue, 'zip:', zipValue);

    // Every role except Agent/Broker edits its single "Address" section through
    // formData.state/city/zipCode, but PUT /api/v1/users/{id} saves those into
    // the PERSONAL fields (UserProfileAddressMutator.applyPersonal) and never
    // touches data.state/city/postalCode — so for these roles the personal
    // fields are the only up-to-date copy. Load from them first; fall back to
    // the general fields only when no personal address exists at all (older
    // accounts), so those still show their registration address as before.
    // Agent/Broker keep state/city/zipCode = LICENSED address, unchanged.
    const roleKey = (userRole || '').toLowerCase();
    const isLicensedAddressRole = agentRoles.includes(roleKey) || brokerRoleSet.includes(roleKey);
    const personalZipValue = data.personalPostalCode || data.personalZipCode || '';
    const hasPersonalAddress = !!(data.personalState || data.personalCity || personalZipValue);
    const addressFromPersonal = !isLicensedAddressRole && hasPersonalAddress;

    // Base form data from user API
    const baseFormData = {
      firstName: data.firstName || '',
      middleName: data.middleName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phone: data.phone || '',
      address: data.street || data.address || '',
      // For a verified Agent/Broker, state/city/zipCode hold the LICENSED
      // address (per the state registry) — see personalState/personalCity/
      // personalZipCode below for the real mailing address. For other roles
      // these hold the mailing address itself (see addressFromPersonal above).
      state: addressFromPersonal ? (data.personalState || '') : stateValue,
      city: addressFromPersonal ? (data.personalCity || '') : (data.city || ''),
      zipCode: addressFromPersonal ? personalZipValue : zipValue,
      personalState: data.personalState || '',
      personalCity: data.personalCity || '',
      personalZipCode: personalZipValue,
      role: userRole,
      // Use profilePhotoBase64 as primary, fallback to profilePhotoUrl
      profilePhotoUrl: data.profilePhotoBase64 || data.profilePhotoUrl || '',
      // Agent personal fields
      agentNumber: data.agentNumber || '',
      memberSince: data.createdAt || '',
      // Initialize agent/broker fields as empty
      licenseNumber: '',
      narId: '',
      brokerageName: '',
      brokerOfRecordName: '',
      brokerLicenseNumber: '',
      brokerageLicenseType: '',
      brokerageAddress: '',
      brokeragePhone: '',
      brokerageNarId: '',
      // Broker company fields
      companyName: '',
      companyLicenseNumber: '',
      companyLicenseType: '',
      companyLicenseState: '',
      companyAddress: '',
      companyPhone: '',
      companyNarId: '',
      // Firm fields (brokerage role)
      firmLegalName: '',
      firmLicenseNumber: '',
      firmLicenseState: '',
      firmDesignatedBrokerName: '',
      firmAddress: '',
      firmPhone: '',
      firmEmail: '',
      firmNarId: '',
      firmBrokerageNarId: '',
      designatedBrokerName: '',
      designatedBrokerEmail: '',
      designatedBrokerPhone: '',
      designatedBrokerStatus: ''
    };

    // If agent data is provided, populate agent-specific fields from agent-profile API
    if (agentData) {
      baseFormData.licenseNumber = agentData.licenseNumber || '';
      baseFormData.narId = agentData.narId || '';
      baseFormData.brokerageName = agentData.brokerageName || '';
      baseFormData.brokerOfRecordName = agentData.brokerOfRecordName || '';
      baseFormData.brokerLicenseNumber = agentData.brokerLicenseNumber || agentData.brokerageLicenseNumber || '';
      baseFormData.brokerageLicenseType = agentData.brokerageLicenseType || '';
      baseFormData.brokerageAddress = agentData.brokerageAddress || '';
      baseFormData.brokeragePhone = agentData.brokeragePhone || '';
      baseFormData.brokerageNarId = agentData.brokerageNarId || '';
    }

    // TC's brokerage fields — reuses the SAME field names as Agent's Brokerage
    // section above (brokerageName/brokerOfRecordName/brokerLicenseNumber/...)
    // since only one of the two roles is ever active at a time. Canonical
    // firm-side join (tcBrokerage) wins over the TC's own self-entered record
    // (tcProfile), matching mobile EditProfileScreen's merge exactly. Phone
    // always comes from the firm join only — TCs don't self-enter it and it
    // stays read-only even in edit mode.
    if (tcData) {
      const { tcProfile, tcBrokerage } = tcData;
      baseFormData.brokerageName = tcBrokerage?.companyName || tcProfile?.brokerageName || '';
      baseFormData.brokerOfRecordName = tcProfile?.brokerOfRecordName || '';
      baseFormData.brokerLicenseNumber = tcBrokerage?.companyLicenseNumber || tcProfile?.brokerageLicenseNumber || '';
      baseFormData.brokerageLicenseType = tcProfile?.brokerageLicenseType || '';
      baseFormData.brokerageAddress = tcBrokerage?.companyAddress || tcProfile?.brokerageAddress || '';
      baseFormData.brokeragePhone = tcBrokerage?.companyPhone || '';
      baseFormData.brokerageNarId = tcBrokerage?.brokerageNarId || tcBrokerage?.narId
        || tcProfile?.brokerageNarId || tcProfile?.narId || '';
      baseFormData.narId = tcProfile?.narId || '';
    }

    // If broker data is provided, populate broker company fields from broker-profile API
    // BROKER shape: { companyName, companyLicenseNumber, companyAddress, companyPhone, narId }
    // BROKER_MANAGER / BROKER_ADMIN shape: { brokerageName, brokerageLicenseNumber }
    if (brokerData) {
      const isBrokerMgrOrAdmin = ['broker_manager', 'broker_admin'].includes(userRole);
      baseFormData.companyName = brokerData.companyName || (isBrokerMgrOrAdmin ? brokerData.brokerageName : '') || '';
      baseFormData.companyLicenseNumber = brokerData.companyLicenseNumber || brokerData.brokerageLicenseNumber || '';
      baseFormData.companyLicenseType = brokerData.companyLicenseType || brokerData.brokerageLicenseType || '';
      baseFormData.companyLicenseState = brokerData.companyLicenseState || brokerData.brokerageLicenseState || '';
      baseFormData.companyAddress = brokerData.companyAddress || '';
      baseFormData.companyPhone = brokerData.companyPhone || '';
      baseFormData.companyNarId = brokerData.narId || '';
      // Broker's own personal DRE license (ACCOUNT section), distinct from the firm's companyLicenseNumber above
      baseFormData.licenseNumber = brokerData.brokerLicenseNumber || '';
    }

    // If brokerage profile data is provided (BROKERAGE role), populate firm fields
    if (brokerageData) {
      baseFormData.firmLegalName = brokerageData.companyName || brokerageData.brokerageName || brokerageData.legalName || '';
      baseFormData.firmLicenseNumber = brokerageData.companyLicenseNumber || brokerageData.brokerLicenseNumber || brokerageData.licenseNumber || '';
      baseFormData.firmLicenseState = brokerageData.companyLicenseState || brokerageData.licenseState || brokerageData.state || '';
      baseFormData.firmDesignatedBrokerName = brokerageData.brokerOfRecordName || brokerageData.designatedBrokerName || '';
      baseFormData.firmAddress = brokerageData.companyAddress || brokerageData.brokerageAddress || brokerageData.address || '';
      baseFormData.firmPhone = brokerageData.companyPhone || brokerageData.brokeragePhone || brokerageData.phone || '';
      baseFormData.firmEmail = brokerageData.companyEmail || '';
      baseFormData.firmNarId = brokerageData.narId || '';
      baseFormData.firmBrokerageNarId = brokerageData.brokerageNarId || '';
    }

    // Designated Broker (read-only) — linked BROKER-role user for this brokerage.
    // If the DB hasn't registered yet, fall back to the name/email captured at
    // brokerage activation (BrokerageProfile.designatedBrokerEmail) and show
    // "Invite pending" — mirrors mobile EditProfileScreen's dbEmailOnFile/dbNotRegistered.
    if (designatedBroker || brokerageData) {
      const linkedName = designatedBroker
        ? `${designatedBroker.firstName || ''} ${designatedBroker.lastName || ''}`.trim()
        : '';
      baseFormData.designatedBrokerName = linkedName || brokerageData?.designatedBrokerName || '';
      baseFormData.designatedBrokerEmail = designatedBroker?.email || brokerageData?.designatedBrokerEmail || '';
      baseFormData.designatedBrokerPhone = designatedBroker?.phone || '';
      baseFormData.designatedBrokerStatus = designatedBroker
        ? (designatedBroker.status || '')
        : (brokerageData?.designatedBrokerEmail ? 'Invite pending' : '');
    }

    setFormData(baseFormData);

    // Derive license state for address autocomplete restriction (matches mobile app behaviour)
    // baseFormData.state (not the raw stateValue) so non-Agent/Broker roles fall
    // back to their current mailing state, not a stale registration value.
    let licenseState = data.licenseState || baseFormData.state;
    if (licenseState && licenseState.length > 2) {
      const found = stateCityData.find(
        item => item.state.toLowerCase() === licenseState.toLowerCase()
      );
      licenseState = found ? found.stateCode : '';
    }
    setUserLicenseState(licenseState ? licenseState.toUpperCase() : '');
  };

  // Fetch agent profile data
  const fetchAgentProfile = async (userId, firebaseToken) => {
    try {
      console.log('Fetching agent profile for userId:', userId);
      const response = await apiClient.get(API_ENDPOINTS.agentProfile(userId), {
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Agent Profile API response:', response.data);
      const agentRaw = response.data;
      return agentRaw?.licenseNumber !== undefined ? agentRaw : (agentRaw?.data || agentRaw);
    } catch (error) {
      console.error('Error fetching agent profile:', error);
      console.error('Agent profile error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return null;
    }
  };

  // Fetch TC's self-entered brokerage profile (analog of fetchAgentProfile for
  // the TRANSACTION_COORDINATOR role). Mirrors mobile authService.fetchTCProfile.
  const fetchTCProfile = async (userId, firebaseToken) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.tcProfile(userId), {
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json'
        }
      });
      const raw = response.data;
      return (raw?.data && typeof raw.data === 'object') ? raw.data : raw;
    } catch (error) {
      console.error('Error fetching TC profile:', error.response?.status, error.message);
      return null;
    }
  };

  // Fetch broker profile data
  const fetchBrokerProfile = async (userId, firebaseToken, activeRole) => {
    try {
      const endpoint = getBrokerProfileEndpoint(userId, activeRole);
      console.log('Fetching broker profile for userId:', userId, 'role:', activeRole, 'endpoint:', endpoint);
      const response = await apiClient.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Broker Profile API response:', response.data);
      const brokerRaw = response.data;
      // Accept the top-level object if it's a plain object; otherwise unwrap nested data
      return (brokerRaw && typeof brokerRaw === 'object' && !Array.isArray(brokerRaw))
        ? (brokerRaw?.companyName !== undefined ? brokerRaw : (brokerRaw?.data || brokerRaw))
        : null;
    } catch (error) {
      console.error('Error fetching broker profile:', error.response?.status, error.message);
      return null;
    }
  };

  // Fetch user profile data from API
  const fetchUserProfile = async () => {
    try {
      // Wait for Firebase auth state — handles async IDB restoration after page reload
      const currentUser = await new Promise((resolve) => {
        if (auth.currentUser) { resolve(auth.currentUser); return; }
        const tid = setTimeout(() => { unsub(); resolve(null); }, 8_000);
        const unsub = auth.onAuthStateChanged(user => { clearTimeout(tid); unsub(); resolve(user); });
      });
      if (!currentUser) {
        navigate('/login');
        return;
      }

      const firebaseToken = await currentUser.getIdToken(true);

      console.log('Fetching user profile from API...');
      const response = await apiClient.get(API_ENDPOINTS.userMe, {
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Profile API response:', response.data);

      if (response.data) {
        // Handle both direct { id, ... } and nested { data: { id, ... } } response formats
        const rawData = response.data;
        const userData = rawData?.id ? rawData : (rawData?.data || rawData);
        let agentData = null;
        let brokerData = null;
        let tcData = null;

        // Fetch role-specific profile based on active role only
        const activeRoleForFetch = getActiveRoleFromStorage()?.toLowerCase() || getUserRole(userData);
        if (agentRoles.includes(activeRoleForFetch) && userData.id) {
          console.log('Active role is agent, fetching agent profile...');
          agentData = await fetchAgentProfile(userData.id, firebaseToken);
        }
        if (brokerRoleSet.includes(activeRoleForFetch) && userData.id) {
          console.log('Active role is broker, fetching broker profile...');
          brokerData = await fetchBrokerProfile(userData.id, firebaseToken, activeRoleForFetch);
        }
        if (activeRoleForFetch === 'transaction_coordinator' && userData.id) {
          // Fan both fetches in parallel and let each fail independently — self-entered
          // fields must render even when the firm-side join misses (firm not registered),
          // and vice versa. Mirrors mobile EditProfileScreen's TC fetch.
          console.log('Active role is TC, fetching TC profile + brokerage...');
          const [tcProfileSettled, tcBrokerageSettled] = await Promise.allSettled([
            fetchTCProfile(userData.id, firebaseToken),
            tcService.getMyBrokerage(firebaseToken),
          ]);
          const tcProfile = tcProfileSettled.status === 'fulfilled' ? tcProfileSettled.value : null;
          const tcBrokerage = tcBrokerageSettled.status === 'fulfilled' ? tcBrokerageSettled.value : null;
          setTcProfileData(tcProfile);
          setTcBrokerageData(tcBrokerage);
          tcData = { tcProfile, tcBrokerage };
        }
        let brokerageData = null;
        let designatedBroker = null;
        if (activeRoleForFetch === 'brokerage') {
          console.log('Active role is brokerage, fetching brokerage profile...');
          brokerageData = await fetchBrokerageProfile(firebaseToken);
          designatedBroker = await fetchDesignatedBroker(firebaseToken, brokerageData);
          setDesignatedBrokerData(designatedBroker);
          setBrokerageProfileData(brokerageData);
        }

        // Combine user data with role-specific profile data for storage
        // IMPORTANT: Preserve the user's id and firebaseUid - don't let profile data overwrite them
        const combinedUserData = {
          ...userData,
          ...(agentData || {}),
          ...(brokerData || {}),
          ...(brokerageData || {}),
          id: userData.id,
          firebaseUid: userData.firebaseUid,
          userId: userData.id
        };

        setUserData(combinedUserData);
        populateFormData(userData, agentData, brokerData, brokerageData, designatedBroker, tcData);

        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(combinedUserData));
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // If API fails, fall back to localStorage data
      const storedUserData = localStorage.getItem('user');
      if (storedUserData) {
        try {
          const parsedData = JSON.parse(storedUserData);
          console.log('Using localStorage data as fallback:', parsedData);
          setUserData(parsedData);
          const agentData = isUserAgent(parsedData) ? parsedData : null;
          const brokerData = isUserBroker(parsedData) ? parsedData : null;
          const brokerageData = isUserBrokerage(parsedData) ? parsedData : null;
          populateFormData(parsedData, agentData, brokerData, brokerageData);
        } catch (parseError) {
          console.error('Error parsing localStorage data:', parseError);
        }
      }

      // If 401 Unauthorized, redirect to login
      if (error.response?.status === 401) {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if user is authenticated
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated !== 'true') {
      navigate('/login');
      return;
    }

    // Fetch user profile from API
    fetchUserProfile();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'licenseNumber' || name === 'brokerLicenseNumber' || name === 'companyLicenseNumber' || name === 'firmLicenseNumber') {
      // License fields: alphanumeric only — no special characters
      setFormData({
        ...formData,
        [name]: value.replace(/[^a-zA-Z0-9]/g, '')
      });
      // Re-arm the Verify button — any edit after a result invalidates it,
      // forcing re-verification before save (mirrors mobile EditProfileScreen).
      if (name === 'brokerLicenseNumber') setBrokerageLicenseVerify({ status: 'idle', message: '' });
      if (name === 'companyLicenseNumber') setCompanyLicenseVerify({ status: 'idle', message: '' });
    } else if (name === 'phone' || name === 'companyPhone' || name === 'brokeragePhone') {
      setFormData({
        ...formData,
        [name]: formatUSPhoneInput(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // ── Brokerage/Company License "Verify" flow ────────────────────────────────
  // Calls the SAME public verify-license API used at Agent/Broker/Brokerage/TC
  // registration (see AgentVerificationPage.js), but here the number being
  // looked up IS the firm's own license (role: 'BROKERAGE'), not the user's
  // personal one — the response's own name/address/licenseType describe the
  // firm, and its `affiliations[0]` (when present) is the Broker of Record.
  // Mirrors mobile EditProfileScreen's verifyBrokerageLicense.
  const runLicenseVerify = async ({ licenseNumber, role = 'BROKERAGE', fallbackToBroker = false, setVerify, applyEnriched }) => {
    const num = (licenseNumber || '').trim();
    if (!num) {
      setVerify({ status: 'invalid', message: 'Enter a license number to verify.' });
      return;
    }
    const stateCode = formData.state;
    if (!stateCode) {
      setVerify({ status: 'invalid', message: 'Set your State (Address section) first — used to look up the brokerage.' });
      return;
    }

    setVerify({ status: 'validating', message: 'Verifying with state registry…' });

    const attempt = async (attemptRole) => {
      const res = await apiClient.post(API_ENDPOINTS.verifyLicense, {
        licenseNumber: num,
        state: stateCode,
        role: attemptRole,
      });
      const data = res.data?.data || res.data;
      const status = data?.status;
      const isMappable = status === 'valid' || status === 'already_used' || data?.isValid === true;
      return { data, isMappable };
    };

    try {
      let { data, isMappable } = await attempt(role);
      if (!isMappable && fallbackToBroker && role === 'BROKERAGE') {
        ({ data, isMappable } = await attempt('BROKER'));
      }
      if (isMappable) {
        const parts = [data.addressLine1, data.city, stateCode, data.zipCode].filter(Boolean);
        applyEnriched({
          name: data.fullName || '',
          address: parts.length ? parts.join(', ') : '',
          licenseType: data.licenseType || '',
          brokerOfRecord: data.affiliations?.[0]?.fullName || '',
        });
        setVerify({ status: 'valid', message: data.fullName ? `Verified: ${data.fullName}` : `Verified: ${num}` });
      } else {
        setVerify({
          status: 'invalid',
          message: data?.verificationDetails || data?.message || 'Unable to verify this license. Please check the number and try again.',
        });
      }
    } catch (error) {
      console.error('[ProfilePage] verify-license error:', error);
      setVerify({
        status: 'invalid',
        message: error?.response?.data?.message || 'Unable to verify this license. Please check the number and try again.',
      });
    }
  };

  // Agent and TC share the same brokerLicenseNumber/brokerageName/etc. fields
  // (mutually exclusive active roles) — Agent gets the Broker↔Brokerage retry
  // fallback mobile applies to its field; TC does not (matches mobile exactly).
  const handleVerifyBrokerageLicense = () => {
    runLicenseVerify({
      licenseNumber: formData.brokerLicenseNumber,
      role: 'BROKERAGE',
      fallbackToBroker: agentRoles.includes(formData.role?.toLowerCase()),
      setVerify: setBrokerageLicenseVerify,
      applyEnriched: (enriched) => setFormData(prev => ({
        ...prev,
        brokerageName: enriched.name || prev.brokerageName,
        brokerageAddress: enriched.address || prev.brokerageAddress,
        brokerageLicenseType: enriched.licenseType || prev.brokerageLicenseType,
        // Always overwrites from the registry, even to clear a stale manual entry.
        brokerOfRecordName: enriched.brokerOfRecord,
      })),
    });
  };

  const handleVerifyCompanyLicense = () => {
    runLicenseVerify({
      licenseNumber: formData.companyLicenseNumber,
      role: 'BROKERAGE',
      fallbackToBroker: false,
      setVerify: setCompanyLicenseVerify,
      applyEnriched: (enriched) => setFormData(prev => ({
        ...prev,
        companyName: enriched.name || prev.companyName,
        companyAddress: enriched.address || prev.companyAddress,
        companyLicenseType: enriched.licenseType || prev.companyLicenseType,
      })),
    });
  };

  const handleAddressSelect = (parsed) => {
    setFormData(prev => ({
      ...prev,
      address: parsed.street || prev.address,
      // For a verified Agent/Broker, a Street pick here is the PERSONAL
      // address — the Licensed Address fields (state/city/zipCode) must stay
      // untouched. For everyone else, state/city/zipCode ARE the only address.
      ...(parsed.stateCode && (isAgent || isBrokerUser)
        ? {
            personalState: parsed.stateCode,
            personalCity: parsed.city || '',
            personalZipCode: parsed.zip || '',
          }
        : parsed.stateCode
        ? {
            state: parsed.stateCode,
            city: parsed.city || '',
            zipCode: parsed.zip || '',
          }
        : {}),
    }));
  };

  // Get state name from stateCode for display
  const getStateNameFromCode = (stateCode) => {
    const stateData = stateCityData.find((item) => item.stateCode === stateCode);
    return stateData ? stateData.state : stateCode;
  };

  // Format createdAt (a bare backend LocalDateTime — UTC digits, no 'Z') as
  // M/D/YYYY in the viewer's own profile-state timezone, not the device's.
  const formatMemberSince = (dateStr) => {
    if (!dateStr) return '';
    const hasZone = /[Zz]|[+-]\d{2}:\d{2}$/.test(dateStr);
    const d = new Date(hasZone ? dateStr : `${dateStr}Z`);
    if (isNaN(d.getTime())) return '';
    const parts = {};
    new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: getViewerTimezone() })
      .formatToParts(d).forEach((p) => { if (p.type !== 'literal') parts[p.type] = p.value; });
    return `${parts.month}/${parts.day}/${parts.year}`;
  };

  // Section visibility: based on active role only
  const isAgent = agentRoles.includes(formData.role?.toLowerCase());
  const isBrokerUser = brokerRoleSet.includes(formData.role?.toLowerCase());
  const isBrokerManagerOrAdmin = ['broker_manager', 'broker_admin'].includes(formData.role?.toLowerCase());
  const isBrokerageRole = formData.role?.toLowerCase() === 'brokerage';
  const isTC = formData.role?.toLowerCase() === 'transaction_coordinator';
  const isBuyerOrSeller = ['buyer', 'seller'].includes(formData.role?.toLowerCase().replace(/^role_/, ''));

  // My Profile sidenav — same per-role visibility the old profile-menu
  // dropdown items used (see DashboardHeader.js's Payments/Connected
  // Services/Activity Log gating), now applied to sidenav rows instead.
  const currentRoleNorm = (getActiveRoleFromStorage() || formData.role || '').toUpperCase();
  // Payments & Payouts: AGENT role only (temporary — 2026-10-14).
  // Broker variants and TC intentionally excluded until the broker-side
  // payment flow is designed. Buyer/Seller never had access.
  const canSeePayments = currentRoleNorm === 'AGENT';
  const canSeeConnected = ['AGENT', 'BROKER', 'LISTING_AGENT', 'BUYER_AGENT', 'TRANSACTION_COORDINATOR'].includes(currentRoleNorm);
  const canSeeActivity = ['AGENT', 'TRANSACTION_COORDINATOR', 'BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'].includes(currentRoleNorm);

  const profileSidenavItems = [
    {
      id: 'info', label: 'Profile Info', show: true,
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>),
    },
    {
      id: 'notifications', label: 'Notification Settings', show: true,
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>),
    },
    {
      id: 'payments', label: 'Payments & Payouts', show: canSeePayments,
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>),
    },
    {
      id: 'connected', label: 'Connected Services', show: canSeeConnected,
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>),
    },
    {
      id: 'activity', label: 'Activity Log', show: canSeeActivity,
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="7" y1="8" x2="17" y2="8"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="7" y1="16" x2="13" y2="16"/></svg>),
    },
    {
      id: 'settings', label: 'Security & Settings', show: true,
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>),
    },
    {
      id: 'privacy', label: 'Privacy Policy', show: true,
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>),
    },
    {
      id: 'terms', label: 'Terms of Service', show: true,
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>),
    },
  ];

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setStatus({ type: 'error', message: 'File size should be less than 5MB' });
        return;
      }
      // Check file type
      if (!file.type.startsWith('image/')) {
        setStatus({ type: 'error', message: 'Please select an image file' });
        return;
      }

      // If in edit mode, just set the image for later upload with save
      if (isEditing) {
        setProfileImage(file);
        // Create a preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData({
            ...formData,
            profilePhotoUrl: reader.result
          });
        };
        reader.readAsDataURL(file);
      } else {
        // If not in edit mode, upload immediately
        await uploadPhotoDirectly(file);
      }
    }
  };

  // Upload photo directly without entering edit mode
  const uploadPhotoDirectly = async (file) => {
    try {
      setUploading(true);
      setStatus({ type: 'info', message: 'Uploading photo...' });

      // Get fresh Firebase token from current user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setStatus({ type: 'error', message: 'You are not logged in. Please log in again.' });
        navigate('/login');
        return;
      }

      const firebaseToken = await currentUser.getIdToken(true);

      // Use userId field first (preserved user ID), then id, then firebaseUid
      const userId = userData.userId || userData.id || userData.firebaseUid;

      console.log('Photo upload - Available IDs:', {
        userId: userData.userId,
        id: userData.id,
        firebaseUid: userData.firebaseUid,
        selectedUserId: userId
      });

      if (!userId) {
        setStatus({ type: 'error', message: 'User ID not found. Please login again.' });
        setUploading(false);
        return;
      }

      console.log('Uploading profile photo directly for user:', userId);

      // Create FormData and upload
      const photoFormData = new FormData();
      photoFormData.append('photo', file);

      const photoResponse = await apiClient.post(
        API_ENDPOINTS.userPhoto(userId),
        photoFormData,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': undefined  // Remove default application/json so browser sets multipart/form-data with boundary
          },
          timeout: 15000
        }
      );

      console.log('Profile photo uploaded successfully:', photoResponse.data);

      // Create base64 preview for immediate display
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Image = reader.result;

        // Update formData with new image
        setFormData(prev => ({
          ...prev,
          profilePhotoUrl: base64Image
        }));

        // Update userData and localStorage
        const updatedUserData = {
          ...userData,
          profilePhotoUrl: photoResponse.data.photoUrl || photoResponse.data.url || photoResponse.data.path,
          profilePhotoBase64: base64Image
        };
        setUserData(updatedUserData);
        localStorage.setItem('user', JSON.stringify(updatedUserData));

        setStatus({ type: 'success', message: 'Profile photo updated successfully!' });

        // Clear success message after 3 seconds
        setTimeout(() => {
          setStatus({ type: '', message: '' });
        }, 3000);
      };
      reader.readAsDataURL(file);

    } catch (error) {
      console.error('Photo upload failed:', error);
      console.error('Photo error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setStatus({ type: 'error', message: error.response?.data?.message || 'Failed to upload photo. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  // Resend the Designated-Broker activation invite. Shown on the DESIGNATED
  // BROKER card when the DB hasn't registered yet (no linked BROKER user) but
  // the brokerage still has an email on file. Same two-step flow as initial
  // activation on BrokerageRegistrationPage: createInvitation writes/refreshes
  // the invitation row, then sendInviteEmail dispatches it — email failure is
  // non-fatal since the invitation row exists either way (mirrors mobile
  // EditProfileScreen.handleResendDbInvite).
  const handleResendDbInvite = async () => {
    if (isResendingDbInvite) return;
    const dbEmail = (brokerageProfileData?.designatedBrokerEmail || formData.designatedBrokerEmail || '').trim();
    if (!dbEmail) {
      setStatus({ type: 'error', message: "We don't have a Designated Broker email on file for this brokerage." });
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setStatus({ type: 'error', message: 'You are not logged in. Please log in again.' });
      navigate('/login');
      return;
    }

    setIsResendingDbInvite(true);
    setStatus({ type: 'info', message: 'Resending invite…' });
    try {
      const firebaseToken = await currentUser.getIdToken(true);
      const authHeaders = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      const firmName = brokerageProfileData?.companyName || formData.firmLegalName || '';

      const inv = await apiClient.post(API_ENDPOINTS.createInvitation, {
        role: 'BROKER',
        email: dbEmail,
        isShareable: false,
        companyName: firmName || undefined,
        brokerageLicenseId: brokerageProfileData?.companyLicenseNumber || formData.firmLicenseNumber || undefined,
        isDesignatedBrokerInvite: true,
      }, { headers: authHeaders });

      const invitationKey = inv.data?.invitationKey || inv.data?.key || inv.data?.data?.invitationKey;
      if (!invitationKey) {
        setStatus({ type: 'warning', message: 'Invite could not be created. Please try again.' });
        return;
      }

      const inviterName = firmName
        || `${formData.firstName || ''} ${formData.lastName || ''}`.trim()
        || 'A Rialty user';
      try {
        await apiClient.post(API_ENDPOINTS.sendInviteEmail, {
          invitationKey,
          email: dbEmail,
          inviteeName: formData.designatedBrokerName || 'Designated Broker',
          role: 'BROKER',
          inviterName,
          inviterEmail: formData.email || '',
          welcomeNote: firmName
            ? `You've been invited to register as the Designated Broker of ${firmName} on Rialty.`
            : 'You\'ve been invited to register as a Designated Broker on Rialty.',
        }, { headers: authHeaders });
        setStatus({ type: 'success', message: `Invite resent to ${dbEmail}` });
      } catch (emailErr) {
        console.warn('[ProfilePage] DB Broker invite email send failed:', emailErr?.message);
        setStatus({ type: 'warning', message: 'The invitation was recorded but the email failed to send. Please try again in a moment.' });
      }
    } catch (err) {
      console.error('[ProfilePage] Resend DB invite failed:', err);
      setStatus({ type: 'error', message: err.response?.data?.message || 'Could not resend the invite. Please try again.' });
    } finally {
      setIsResendingDbInvite(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setProfileImage(null);
    setBrokerageLicenseVerify({ status: 'idle', message: '' });
    setCompanyLicenseVerify({ status: 'idle', message: '' });
    // Reset form data to original userData
    if (userData) {
      const agentData = isUserAgent(userData) ? userData : null;
      const brokerData = isUserBroker(userData) ? userData : null;
      const brokerageData = isUserBrokerage(userData) ? userData : null;
      const tcData = isTC ? { tcProfile: tcProfileData, tcBrokerage: tcBrokerageData } : null;
      populateFormData(userData, agentData, brokerData, brokerageData, designatedBrokerData, tcData);
    }
  };

  const handleSave = async () => {
    try {
      setUploading(true);
      setStatus({ type: 'info', message: 'Saving profile...' });

      // Get fresh Firebase token from current user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setStatus({ type: 'error', message: 'You are not logged in. Please log in again.' });
        navigate('/login');
        return;
      }

      const firebaseToken = await currentUser.getIdToken(true);

      // Use userId field first (preserved user ID), then id, then firebaseUid
      const userId = userData.userId || userData.id || userData.firebaseUid;

      console.log('Profile save - Available IDs:', {
        userId: userData.userId,
        id: userData.id,
        firebaseUid: userData.firebaseUid,
        selectedUserId: userId
      });

      if (!userId) {
        setStatus({ type: 'error', message: 'User ID not found. Please login again.' });
        setUploading(false);
        return;
      }

      let backendResponse = null;

      // Step 1: Update user profile data using PUT request
      console.log('Updating profile data for user:', userId);

      // User profile data - for all users (matches mobile's authService.updateProfile
      // payload exactly). Sent to the plain /api/v1/users/{id} route — NOT
      // /api/v1/users/{id}/profile — mirroring mobile's AUTH.UPDATE_PROFILE endpoint.
      // The /profile sub-route was found not to persist `phone` (it reverted after
      // reload while every other field saved fine); mobile's EditProfileScreen has
      // never hit that route and has never shown this symptom.
      const userProfileData = {
        firstName: formData.firstName,
        middleName: formData.middleName || null,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone ? toE164US(formData.phone) : formData.phone,
        agentNumber: formData.agentNumber || null,
        street: formData.address || null,
        // The backend's PUT /api/v1/users/{id} always writes city/state/postalCode
        // here into the PERSONAL address fields (never the licensed address — see
        // UserProfileAddressMutator.applyPersonal). For Agent/Broker, formData.city/
        // state/zipCode hold the LICENSED address instead, so send the personal
        // ones here to avoid overwriting their real mailing address with license
        // data on every save.
        city: ((isAgent || isBrokerUser) ? formData.personalCity : formData.city) || null,
        state: ((isAgent || isBrokerUser) ? formData.personalState : formData.state) || null,
        postalCode: ((isAgent || isBrokerUser) ? formData.personalZipCode : formData.zipCode) || null,
      };

      try {
        const response = await apiClient.put(
          API_ENDPOINTS.userById(userId),
          userProfileData,
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              'Content-Type': 'application/json'
            },
            timeout: 10000
          }
        );

        console.log('User profile updated successfully:', response.data);
        backendResponse = response.data;
      } catch (apiError) {
        console.error('User profile update error:', apiError);
        console.error('Error details:', {
          message: apiError.message,
          response: apiError.response?.data,
          status: apiError.response?.status
        });
        setStatus({ type: 'error', message: apiError.response?.data?.message || 'Failed to update profile. Please try again.' });
        setUploading(false);
        return;
      }

      // Step 2: Update role-specific profiles independently — user may hold both Agent and Broker roles
      if (isAgent) {
        console.log('Updating agent profile for user:', userId);

        // Matches mobile's authService.updateAgentProfile payload exactly: trimmed,
        // empty fields omitted (undefined) rather than sent as null.
        const agentProfileData = {
          brokerageName: formData.brokerageName?.trim() || undefined,
          brokerOfRecordName: formData.brokerOfRecordName?.trim() || undefined,
          brokerageLicenseNumber: formData.brokerLicenseNumber?.trim() || undefined,
          brokerageLicenseType: formData.brokerageLicenseType?.trim() || undefined,
          brokerageAddress: formData.brokerageAddress?.trim() || undefined,
          brokeragePhone: formData.brokeragePhone?.trim() ? toE164US(formData.brokeragePhone.trim()) : undefined,
          narId: formData.narId?.trim() || undefined,
          brokerageNarId: formData.brokerageNarId?.trim() || undefined,
        };

        try {
          const agentResponse = await apiClient.put(
            API_ENDPOINTS.agentProfile(userId),
            agentProfileData,
            {
              headers: {
                'Authorization': `Bearer ${firebaseToken}`,
                'Content-Type': 'application/json'
              },
              timeout: 10000
            }
          );
          console.log('Agent profile updated successfully:', agentResponse.data);
        } catch (agentApiError) {
          console.error('Agent profile update error:', agentApiError);
          setStatus({ type: 'warning', message: 'Profile updated but agent information update failed. You can try again.' });
        }
      }
      if (isBrokerUser) {
        console.log('Updating broker profile for user:', userId, 'role:', formData.role);

        // BROKER_MANAGER / BROKER_ADMIN use brokerageName/brokerageLicenseNumber per mobile spec.
        // BROKER uses companyName/companyLicenseNumber.
        const brokerProfileData = isBrokerManagerOrAdmin
          ? {
              brokerageName: formData.companyName || null,
              brokerageLicenseNumber: formData.companyLicenseNumber || null,
              brokerageLicenseState: formData.companyLicenseState || null,
            }
          : {
              companyName: formData.companyName || null,
              companyLicenseNumber: formData.companyLicenseNumber || null,
              companyLicenseType: formData.companyLicenseType || null,
              companyAddress: formData.companyAddress || null,
              companyPhone: formData.companyPhone ? toE164US(formData.companyPhone) : null,
              narId: formData.companyNarId || null,
            };

        try {
          const brokerResponse = await apiClient.put(
            getBrokerProfileEndpoint(userId, formData.role),
            brokerProfileData,
            {
              headers: {
                'Authorization': `Bearer ${firebaseToken}`,
                'Content-Type': 'application/json'
              },
              timeout: 10000
            }
          );
          console.log('Broker profile updated successfully:', brokerResponse.data);
        } catch (brokerApiError) {
          console.error('Broker profile update error:', brokerApiError);
          setStatus({ type: 'warning', message: 'Profile updated but broker information update failed. You can try again.' });
        }
      }

      // Step 2a: Update TC's self-entered brokerage fields — mirrors mobile's
      // authService.updateTCProfile, PUT to the same /tc-profile route the
      // initial fetch uses. Phone is intentionally omitted: it's always the
      // firm-side joined value (tcBrokerageData.companyPhone), never self-entered.
      if (isTC) {
        console.log('Updating TC brokerage profile for user:', userId);
        const tcProfilePayload = {
          brokerageName: formData.brokerageName?.trim() || undefined,
          brokerOfRecordName: formData.brokerOfRecordName?.trim() || undefined,
          brokerageLicenseNumber: formData.brokerLicenseNumber?.trim() || undefined,
          brokerageLicenseState: formData.state || undefined,
          brokerageLicenseType: formData.brokerageLicenseType?.trim() || undefined,
          brokerageAddress: formData.brokerageAddress?.trim() || undefined,
          narId: formData.narId?.trim() || undefined,
          brokerageNarId: formData.brokerageNarId?.trim() || undefined,
        };

        try {
          await apiClient.put(
            API_ENDPOINTS.tcProfile(userId),
            tcProfilePayload,
            {
              headers: {
                'Authorization': `Bearer ${firebaseToken}`,
                'Content-Type': 'application/json'
              },
              timeout: 10000
            }
          );
          console.log('TC brokerage profile updated successfully');
          setTcProfileData(prev => ({ ...(prev || {}), ...tcProfilePayload }));
        } catch (tcApiError) {
          console.error('TC brokerage profile update error:', tcApiError);
          setStatus({ type: 'warning', message: 'Profile updated but brokerage information update failed. You can try again.' });
        }
      }

      // Step 2b: Update brokerage firm profile for BROKERAGE role.
      // Mobile has no UI to edit these fields on this screen either — it just
      // round-trips the values it fetched. Matches authService.updateMyBrokerageProfile
      // payload exactly: trimmed, empty fields omitted rather than sent as null.
      if (isBrokerageRole) {
        console.log('Updating brokerage firm profile...');
        const brokerageProfileData = {
          companyName: formData.firmLegalName?.trim() || undefined,
          companyLicenseNumber: formData.firmLicenseNumber?.trim() || undefined,
          companyAddress: formData.firmAddress?.trim() || undefined,
          companyPhone: formData.firmPhone?.trim() ? toE164US(formData.firmPhone.trim()) : undefined,
          narId: formData.firmNarId?.trim() || undefined,
        };
        try {
          await apiClient.put(API_ENDPOINTS.brokerageProfile, brokerageProfileData, {
            headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' },
            timeout: 10000
          });
          console.log('Brokerage firm profile updated successfully');
        } catch (brgError) {
          console.error('Brokerage profile update error:', brgError);
          setStatus({ type: 'warning', message: 'Profile updated but firm details update failed. You can try again.' });
        }
      }

      // Step 3: Upload photo if a new image was selected
      let imageUrl = formData.profilePhotoUrl;
      if (profileImage) {
        console.log('Uploading profile photo...');
        try {
          const photoFormData = new FormData();
          photoFormData.append('file', profileImage);

          const photoResponse = await apiClient.post(
            API_ENDPOINTS.userPhoto(userId),
            photoFormData,
            {
              headers: {
                'Authorization': `Bearer ${firebaseToken}`,
                'Content-Type': undefined  // Remove default application/json so browser sets multipart/form-data with boundary
              },
              timeout: 15000
            }
          );

          console.log('Profile photo uploaded successfully:', photoResponse.data);
          imageUrl = photoResponse.data.photoUrl || photoResponse.data.url || photoResponse.data.path;
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
          console.error('Photo error details:', {
            message: photoError.message,
            response: photoError.response?.data,
            status: photoError.response?.status
          });
          setStatus({ type: 'warning', message: 'Profile updated but photo upload failed. You can try uploading the photo again.' });
        }
      }

      // Step 4: Update localStorage with new data
      // If a new image was uploaded, use the base64 from formData (set during handleImageChange)
      // Otherwise keep the existing profilePhotoBase64
      const newProfilePhotoBase64 = profileImage ? formData.profilePhotoUrl : (userData.profilePhotoBase64 || '');

      // Fields the user just edited and submitted are pinned last so a partial or
      // stale echo in `backendResponse` (e.g. a field the PUT response doesn't
      // round-trip) can never silently overwrite what was just saved — this is
      // exactly the class of bug that made the phone field appear to revert.
      const updatedUserData = {
        ...userData,
        ...formData,
        ...(backendResponse || {}),
        firstName: formData.firstName,
        middleName: formData.middleName || '',
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        agentNumber: formData.agentNumber || '',
        address: formData.address || '',
        city: formData.city || '',
        state: formData.state || '',
        zipCode: formData.zipCode || '',
        // Non-Agent/Broker: the Address section was just saved into the
        // personal fields, which populateFormData reads first — pin them too so
        // Cancel/re-populate shows what was saved, not the PUT response's echo.
        ...(!(isAgent || isBrokerUser) ? {
          personalState: formData.state || '',
          personalCity: formData.city || '',
          personalPostalCode: formData.zipCode || '',
        } : {}),
        profilePhotoUrl: imageUrl,
        profilePhotoBase64: newProfilePhotoBase64
      };
      localStorage.setItem('user', JSON.stringify(updatedUserData));
      setUserData(updatedUserData);
      setFormData({
        ...formData,
        profilePhotoUrl: newProfilePhotoBase64 || imageUrl
      });
      setProfileImage(null);
      setIsEditing(false);
      setBrokerageLicenseVerify({ status: 'idle', message: '' });
      setCompanyLicenseVerify({ status: 'idle', message: '' });
      setStatus({ type: 'success', message: 'Profile updated successfully!' });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setStatus({ type: '', message: '' });
      }, 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setStatus({ type: 'error', message: 'Failed to save profile. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  if (loading || !userData) {
    return (
      <div className="profile-page">
          <main className="profile-main-content">
          <div className="profile-container">
            <p className="loading-text">Loading profile...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="profile-page">

      <main className="profile-main-content">
        <div className="my-profile-layout">
          <aside className="my-profile-sidenav">
            <div className="my-profile-sidenav-title">My Profile</div>
            {profileSidenavItems.filter(item => item.show).map(item => (
              <button
                key={item.id}
                type="button"
                className={`my-profile-sidenav-item${activeProfileSection === item.id ? ' current' : ''}`}
                onClick={() => setActiveProfileSection(item.id)}
              >
                <span className="my-profile-sidenav-icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </aside>

          <div className="my-profile-content">
          {activeProfileSection === 'info' && (
        <div className="profile-container">
          <div className="profile-header">
            <button type="button" className="profile-back-btn" onClick={() => navigate(-1)}>
              <span>←</span>
              <span>Back</span>
            </button>
            <h1 className="profile-title">My Profile</h1>
            <p className="profile-subtitle">View and manage your personal information</p>
            <div className="profile-avatar-container">
              <div className="profile-avatar">
                {formData.profilePhotoUrl ? (
                  <img
                    src={formData.profilePhotoUrl}
                    alt="Profile"
                    className="profile-avatar-image"
                  />
                ) : (
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                )}
                {uploading && (
                  <div className="photo-uploading-overlay">
                    <span>Uploading...</span>
                  </div>
                )}
              </div>
              {/* Always show Change Photo button */}
              <div className="image-upload-section">
                <label htmlFor="profile-image-input" className={`image-upload-label ${uploading ? 'disabled' : ''}`}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  {uploading ? 'Uploading...' : 'Change Photo'}
                </label>
                <input
                  id="profile-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="image-upload-input"
                  disabled={uploading}
                />
                {/* Edit / Save / Cancel next to Change Photo so they're visible on
                    page load — same handlers as the bottom profile-actions bar. */}
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      className="image-upload-label profile-inline-action"
                      onClick={handleSave}
                      disabled={uploading}
                    >
                      {uploading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      className="image-upload-label profile-inline-action profile-inline-action--secondary"
                      onClick={handleCancel}
                      disabled={uploading}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="image-upload-label profile-inline-action"
                    onClick={handleEdit}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Status Message */}
          {status.message && (
            <div className={`profile-status-message ${status.type}`}>
              {status.type === 'success' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              )}
              {status.type === 'error' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              )}
              {status.type === 'info' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              )}
              {status.type === 'warning' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              )}
              <span>{status.message}</span>
            </div>
          )}

          <div className="profile-content">
            <div className="profile-info-section">
              <h2 className="section-title">Personal Information</h2>

              <div className="info-grid">
                <div className="info-field">
                  <label className="info-label">First Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="firstName"
                      className="profile-input"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Enter first name"
                    />
                  ) : (
                    <p className="info-value">{formData.firstName || ''}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Middle Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="middleName"
                      className="profile-input"
                      value={formData.middleName}
                      onChange={handleChange}
                      placeholder="Enter middle name (optional)"
                    />
                  ) : (
                    <p className="info-value">{formData.middleName || ''}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Last Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="lastName"
                      className="profile-input"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Enter last name"
                    />
                  ) : (
                    <p className="info-value">{formData.lastName || ''}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Email</label>
                  <p className="info-value">{formData.email}</p>
                </div>

                <div className="info-field">
                  <label className="info-label">Phone</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      className="profile-input"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                    />
                  ) : (
                    <p className="info-value">{formData.phone || ''}</p>
                  )}
                </div>

                {/* NAR ID - agents only (License Number lives in the Account section) */}
                {isAgent && (
                  <div className="info-field">
                    <label className="info-label">NAR ID</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="narId"
                        className="profile-input"
                        value={formData.narId}
                        onChange={handleChange}
                        placeholder="Enter NAR ID (optional)"
                      />
                    ) : (
                      <p className="info-value">{formData.narId || ''}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {(isAgent || isBrokerUser) && (
              <div className="profile-info-section">
                {/* Licensed Address — the state/city/zip this license is registered
                    under (per the state registry), read-only. Distinct from Personal
                    Address below, which can legitimately be in a different state —
                    a Colorado license doesn't require a Colorado mailing address. */}
                <h2 className="section-title">Licensed Address</h2>

                <div className="info-grid">
                  <div className="info-field">
                    <label className="info-label">State</label>
                    <p className="info-value">{getStateNameFromCode(formData.state) || ''}</p>
                  </div>

                  <div className="info-field">
                    <label className="info-label">City</label>
                    <p className="info-value">{formData.city || ''}</p>
                  </div>

                  <div className="info-field">
                    <label className="info-label">ZIP Code</label>
                    <p className="info-value">{formData.zipCode || ''}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="profile-info-section">
              <h2 className="section-title">{(isAgent || isBrokerUser) ? 'Personal Address' : 'Address'}</h2>

              <div className="info-grid">
                <div className="info-field">
                  <label className="info-label">Street</label>
                  {isEditing ? (
                    <AddressAutocomplete
                      value={formData.address}
                      onChange={(text) => handleChange({ target: { name: 'address', value: text } })}
                      onAddressSelect={handleAddressSelect}
                      placeholder="Enter street address"
                      inputClassName="profile-input"
                      // Personal Address can legitimately be in a different state than
                      // the license (see comment on Licensed Address above), and a
                      // Buyer/Seller/Brokerage/TC user's own address isn't tied to a
                      // license either — all can enter any address (matches mobile,
                      // which never restricts this field). Other roles keep the state
                      // restriction.
                      restrictToStateCode={(isAgent || isBrokerUser || isBuyerOrSeller || isBrokerageRole || isTC) ? undefined : (userLicenseState || undefined)}
                    />
                  ) : (
                    <p className="info-value">{formData.address || ''}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">State</label>
                  {isEditing ? (
                    <select
                      name={(isAgent || isBrokerUser) ? 'personalState' : 'state'}
                      className="profile-input"
                      value={(isAgent || isBrokerUser) ? formData.personalState : formData.state}
                      onChange={handleChange}
                    >
                      <option value="">Select state</option>
                      {stateCityData.map((item) => (
                        <option key={item.stateCode} value={item.stateCode}>{item.state}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="info-value">
                      {getStateNameFromCode((isAgent || isBrokerUser) ? formData.personalState : formData.state) || ''}
                    </p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">City</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name={(isAgent || isBrokerUser) ? 'personalCity' : 'city'}
                      className="profile-input"
                      value={(isAgent || isBrokerUser) ? formData.personalCity : formData.city}
                      onChange={handleChange}
                      placeholder="Enter city"
                    />
                  ) : (
                    <p className="info-value">{((isAgent || isBrokerUser) ? formData.personalCity : formData.city) || ''}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">ZIP Code</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name={(isAgent || isBrokerUser) ? 'personalZipCode' : 'zipCode'}
                      className="profile-input"
                      value={(isAgent || isBrokerUser) ? formData.personalZipCode : formData.zipCode}
                      onChange={handleChange}
                      placeholder="Enter zip code"
                    />
                  ) : (
                    <p className="info-value">{((isAgent || isBrokerUser) ? formData.personalZipCode : formData.zipCode) || ''}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Brokerage Section - TCs only. Editable, with a Verify button on
                the Brokerage License # field — matches mobile EditProfileScreen's
                TC branch exactly. Phone stays read-only (always the firm-side
                joined value, never self-entered). */}
            {isTC && (
              <div className="profile-info-section">
                <h2 className="section-title">Brokerage</h2>
                <div className="info-grid">
                  <div className="info-field">
                    <label className="info-label">Brokerage</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="brokerageName"
                        className="profile-input"
                        value={formData.brokerageName}
                        onChange={handleChange}
                        placeholder="Enter brokerage name"
                      />
                    ) : (
                      <p className="info-value">{formData.brokerageName || ''}</p>
                    )}
                  </div>

                  <div className="info-field">
                    <label className="info-label">Broker of Record</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="brokerOfRecordName"
                        className="profile-input"
                        value={formData.brokerOfRecordName}
                        onChange={handleChange}
                        placeholder="Enter broker of record name"
                      />
                    ) : (
                      <p className="info-value">{formData.brokerOfRecordName || ''}</p>
                    )}
                  </div>

                  <LicenseVerifyField
                    label="Brokerage License #"
                    name="brokerLicenseNumber"
                    value={formData.brokerLicenseNumber}
                    onChange={handleChange}
                    verify={brokerageLicenseVerify}
                    onVerify={handleVerifyBrokerageLicense}
                    isEditing={isEditing}
                  />

                  <div className="info-field">
                    <label className="info-label">License Type</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="brokerageLicenseType"
                        className="profile-input"
                        value={formData.brokerageLicenseType}
                        onChange={handleChange}
                        placeholder="Enter license type"
                      />
                    ) : (
                      <p className="info-value">{formData.brokerageLicenseType || ''}</p>
                    )}
                  </div>

                  <div className="info-field">
                    <label className="info-label">Address</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="brokerageAddress"
                        className="profile-input"
                        value={formData.brokerageAddress}
                        onChange={handleChange}
                        placeholder="Enter brokerage address"
                      />
                    ) : (
                      <p className="info-value">{formData.brokerageAddress || ''}</p>
                    )}
                  </div>

                  <div className="info-field">
                    <label className="info-label">Phone</label>
                    <p className="info-value">{formData.brokeragePhone || ''}</p>
                  </div>

                  <div className="info-field">
                    <label className="info-label">NAR ID</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="brokerageNarId"
                        className="profile-input"
                        value={formData.brokerageNarId}
                        onChange={handleChange}
                        placeholder="Enter brokerage NAR ID"
                      />
                    ) : (
                      <p className="info-value">{formData.brokerageNarId || ''}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Company Section - brokers only */}
            {isBrokerUser && (
              <div className="profile-info-section">
                <h2 className="section-title">Company</h2>
                <div className="info-grid">
                  <div className="info-field">
                    <label className="info-label">Company Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="companyName"
                        className="profile-input"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="Enter company name"
                      />
                    ) : (
                      <p className="info-value">{formData.companyName || ''}</p>
                    )}
                  </div>

                  {isBrokerManagerOrAdmin ? (
                    <div className="info-field">
                      <label className="info-label">License Number</label>
                      {isEditing ? (
                        <input
                          type="text"
                          name="companyLicenseNumber"
                          className="profile-input"
                          value={formData.companyLicenseNumber}
                          onChange={handleChange}
                          placeholder="Enter license number"
                        />
                      ) : (
                        <p className="info-value">{formData.companyLicenseNumber || ''}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Only the plain BROKER role gets the Verify button — Broker
                          Manager/Admin staff use a simpler read/plain-edit field
                          (matches mobile EditProfileScreen exactly). */}
                      <LicenseVerifyField
                        label="License Number"
                        name="companyLicenseNumber"
                        value={formData.companyLicenseNumber}
                        onChange={handleChange}
                        verify={companyLicenseVerify}
                        onVerify={handleVerifyCompanyLicense}
                        isEditing={isEditing}
                      />

                      <div className="info-field">
                        <label className="info-label">License Type</label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="companyLicenseType"
                            className="profile-input"
                            value={formData.companyLicenseType}
                            onChange={handleChange}
                            placeholder="Enter license type"
                          />
                        ) : (
                          <p className="info-value">{formData.companyLicenseType || ''}</p>
                        )}
                      </div>

                      <div className="info-field">
                        <label className="info-label">Address</label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="companyAddress"
                            className="profile-input"
                            value={formData.companyAddress}
                            onChange={handleChange}
                            placeholder="Enter company address"
                          />
                        ) : (
                          <p className="info-value">{formData.companyAddress || ''}</p>
                        )}
                      </div>

                      <div className="info-field">
                        <label className="info-label">Phone</label>
                        {isEditing ? (
                          <input
                            type="tel"
                            name="companyPhone"
                            className="profile-input"
                            value={formData.companyPhone}
                            onChange={handleChange}
                            placeholder="Enter company phone"
                          />
                        ) : (
                          <p className="info-value">{formData.companyPhone || ''}</p>
                        )}
                      </div>

                      <div className="info-field">
                        <label className="info-label">NAR ID</label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="companyNarId"
                            className="profile-input"
                            value={formData.companyNarId}
                            onChange={handleChange}
                            placeholder="Enter NAR ID"
                          />
                        ) : (
                          <p className="info-value">{formData.companyNarId || ''}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Brokerage Section - agents only */}
            {isAgent && (
              <div className="profile-info-section">
                <h2 className="section-title">Brokerage</h2>

                <div className="info-grid">
                  <div className="info-field">
                    <label className="info-label">Brokerage</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="brokerageName"
                        className="profile-input"
                        value={formData.brokerageName}
                        onChange={handleChange}
                        placeholder="Enter brokerage name"
                      />
                    ) : (
                      <p className="info-value">{formData.brokerageName || ''}</p>
                    )}
                  </div>

                  <div className="info-field">
                    <label className="info-label">Broker of Record</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="brokerOfRecordName"
                        className="profile-input"
                        value={formData.brokerOfRecordName}
                        onChange={handleChange}
                        placeholder="Enter broker of record name"
                      />
                    ) : (
                      <p className="info-value">{formData.brokerOfRecordName || ''}</p>
                    )}
                  </div>

                  <LicenseVerifyField
                    label="Brokerage License #"
                    name="brokerLicenseNumber"
                    value={formData.brokerLicenseNumber}
                    onChange={handleChange}
                    verify={brokerageLicenseVerify}
                    onVerify={handleVerifyBrokerageLicense}
                    isEditing={isEditing}
                  />

                  <div className="info-field">
                    <label className="info-label">License Type</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="brokerageLicenseType"
                        className="profile-input"
                        value={formData.brokerageLicenseType}
                        onChange={handleChange}
                        placeholder="Enter license type"
                      />
                    ) : (
                      <p className="info-value">{formData.brokerageLicenseType || ''}</p>
                    )}
                  </div>

                  <div className="info-field">
                    <label className="info-label">Address</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="brokerageAddress"
                        className="profile-input"
                        value={formData.brokerageAddress}
                        onChange={handleChange}
                        placeholder="Enter brokerage address"
                      />
                    ) : (
                      <p className="info-value">{formData.brokerageAddress || ''}</p>
                    )}
                  </div>

                  <div className="info-field">
                    <label className="info-label">Phone</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="brokeragePhone"
                        className="profile-input"
                        value={formData.brokeragePhone}
                        onChange={handleChange}
                        placeholder="Enter brokerage phone"
                      />
                    ) : (
                      <p className="info-value">{formData.brokeragePhone || ''}</p>
                    )}
                  </div>

                  <div className="info-field">
                    <label className="info-label">NAR ID</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="brokerageNarId"
                        className="profile-input"
                        value={formData.brokerageNarId}
                        onChange={handleChange}
                        placeholder="Enter brokerage NAR ID"
                      />
                    ) : (
                      <p className="info-value">{formData.brokerageNarId || ''}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Designated Broker Section - brokerage role only. Always read-only:
                a separate BROKER-role user linked to the firm; the brokerage
                doesn't edit their record here (matches mobile exactly). */}
            {isBrokerageRole && (
              <div className="profile-info-section">
                <h2 className="section-title">Designated Broker</h2>
                <div className="info-grid">
                  <div className="info-field">
                    <label className="info-label">Name</label>
                    <p className="info-value">{formData.designatedBrokerName || ''}</p>
                  </div>

                  <div className="info-field">
                    <label className="info-label">Email</label>
                    <p className="info-value">{formData.designatedBrokerEmail || ''}</p>
                  </div>

                  <div className="info-field">
                    <label className="info-label">Phone</label>
                    <p className="info-value">{formData.designatedBrokerPhone || ''}</p>
                  </div>

                  <div className="info-field">
                    <label className="info-label">Status</label>
                    <p className="info-value">{formData.designatedBrokerStatus || ''}</p>
                  </div>
                </div>

                {/* Resend invite — shown when the DB hasn't registered yet but
                    an email is on file (mirrors mobile's Resend-invite button). */}
                {!designatedBrokerData && !!formData.designatedBrokerEmail && (
                  <button
                    type="button"
                    className="profile-resend-invite-btn"
                    onClick={handleResendDbInvite}
                    disabled={isResendingDbInvite}
                  >
                    {isResendingDbInvite ? 'Resending invite…' : 'Resend invite'}
                  </button>
                )}
              </div>
            )}

            {/* Account Section */}
            <div className="profile-info-section">
              <h2 className="section-title">Account</h2>
              <div className="info-grid">
                <div className="info-field">
                  <label className="info-label">Role</label>
                  <p className="info-value">{formData.role?.toUpperCase()}</p>
                </div>
                {(isAgent || (isBrokerUser && !isBrokerManagerOrAdmin) || isBrokerageRole) && (
                  <div className="info-field">
                    <label className="info-label">License Number</label>
                    <p className="info-value">
                      {(isAgent
                        ? formData.agentNumber
                        : isBrokerageRole
                        ? formData.firmLicenseNumber
                        : formData.licenseNumber) || ''}
                    </p>
                  </div>
                )}
                <div className="info-field">
                  <label className="info-label">Member Since</label>
                  <p className="info-value">{formatMemberSince(formData.memberSince)}</p>
                </div>
              </div>
            </div>

            <div className="profile-actions">
              {isEditing ? (
                <>
                  <button className="btn-save" onClick={handleSave} disabled={uploading}>
                    {uploading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button className="btn-cancel" onClick={handleCancel} disabled={uploading}>
                    Cancel
                  </button>
                </>
              ) : (
                <button className="btn-edit" onClick={handleEdit}>
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>
          )}

          {activeProfileSection === 'notifications' && <NotificationSettingsPage />}
          {activeProfileSection === 'payments' && canSeePayments && <PaymentsPage />}
          {activeProfileSection === 'connected' && canSeeConnected && <ConnectedServicesPage />}
          {activeProfileSection === 'activity' && canSeeActivity && <UserActivityPage />}
          {activeProfileSection === 'settings' && <SettingsPage />}
          {activeProfileSection === 'privacy' && <PrivacyPolicyPage showBackButton />}
          {activeProfileSection === 'terms' && <TermsOfServicePage showBackButton />}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
